<script setup lang="ts">
import { inject, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useModalComposable, useViewComposable } from '@/views/composable';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import cloneDeep from 'lodash/cloneDeep';
import InputNumber from 'primevue/inputnumber';
import MultiSelect from 'primevue/multiselect';
import CloseIcon from '@/components/common/CloseIcon.vue';

const dialogRef = inject('dialogRef') as Ref;
const destinations = dialogRef?.value?.options?.data?.destinations;
const hotels = dialogRef?.value?.options?.data?.hotels;
const maxRoomConfigs = dialogRef?.value?.options?.data?.maxRoomConfigs;
const onClose = dialogRef?.value?.options?.emits?.onClose;
const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const { brochureListOption } = useViewComposable();
const { handleOpenErrorMessage } = useModalComposable();
const adminSetupStore = useAdminSetupStore();
const toast = useToast();
const configs = ref(cloneDeep(maxRoomConfigs));
const isShowValidation = ref(false);

const hotelsNew = hotels.map((hotel: any) => ({
  ...hotel,
  fullLabel: `${hotel.hCode} - ${getTextVariant(currentLanguage, hotel.name, hotel.nameFr)}`
}));

const destinationsNew = destinations.map((destination: any) => ({
  ...destination,
  fullLabel: `${destination.airportCode} - ${getTextVariant(currentLanguage, destination.name, destination.nameFr)}`
}));

const checkFormInvalid = () => {
  let isInValid = false;
  configs.value.forEach((config: any) => {
    if ((!config.brochureCodes.length && !config.destinationCodes.length && !config.hotelCodes.length) || !config.maxRooms) {
      isInValid = true;
    }
  });
  return isInValid;
};
const handleAddNewConfig = () => {
  configs.value.push({
    brochureCodes: [],
    destinationCodes: [],
    hotelCodes: [],
    roomCodes: [],
    maxRooms: 0,
    isDeleted: false
  });
};

const handleRemoveConfig = (index: number) => {
  if (configs.value[index].pk && configs.value[index].sk) {
    configs.value[index].isDeleted = true;
  } else {
    configs.value = configs.value.filter((item: any, indexConfig: number) => index !== indexConfig);
  }
};

const checkInvalidNoHotelCode = () => {
  let isInvalid = false;
  configs.value.forEach((config: any) => {
    if (config.roomCodes.length && !config.hotelCodes.length) {
      isInvalid = true;
    }
  });
  return isInvalid;
};

const handleSubmit = () => {
  isShowValidation.value = true;
  if (checkFormInvalid()) {
    return;
  }
  if (checkInvalidNoHotelCode()) {
    handleOpenErrorMessage(t('quote_config.associated_hotel_notification'));
    return;
  }
  adminSetupStore
    .updateMaxRoomConfig(configs.value)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      onClose();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const isInValidForm = (config: any) => {
  return isShowValidation.value && !config.brochureCodes.length && !config.destinationCodes.length && !config.hotelCodes.length;
};

const updateRoomCategory = (event: any, index: number) => {
  if (event.length) {
    const filtered = event.filter((item: string) => item.length < 5 && item.length > 0);
    configs.value[index].roomCodes = [...new Set(filtered)];
  }
};

const checkNoData = () => {
  return !configs.value.length || (configs.value.length && configs.value.every((item: any) => item.isDeleted));
};

const displayDataDestination = (code: any) => {
  return destinationsNew.find((destination: any) => {
    return destination.code === code;
  })?.airportCode;
};
</script>

<template>
  <div>
    <div>
      {{ $t('quote_config.max_room_subtitle_first') }}
    </div>
    <div class="mb-3">
      {{ $t('quote_config.max_room_subtitle_second') }}
    </div>
    <div class="flex" v-if="!checkNoData()">
      <div class="col-2">{{ $t('quote_config.brochures') }}</div>
      <div class="col-3">{{ $t('quote_config.destinations') }}</div>
      <div class="col-3">{{ $t('quote_config.hotel_code') }}</div>
      <div class="col-2">{{ $t('book_form.room_category') }}</div>
      <div class="col-2">{{ $t('quote_config.max_rooms') }}</div>
    </div>
    <div v-for="(config, index) in configs" :key="index">
      <div v-if="!config.isDeleted" class="flex mb-2">
        <div class="col-2">
          <MultiSelect display="chip" :maxSelectedLabels="3" :placeholder="$t('management.package.brochure')" v-model="config.brochureCodes" :options="brochureListOption" class="w-full" :invalid="isInValidForm(config)">
            <template #option="slotProps">
              <p>
                {{ slotProps.option }}
              </p>
            </template>
          </MultiSelect>
          <p v-if="isInValidForm(config)" class="text-red-500 pt-1">{{ $t('validate.required') }}</p>
        </div>
        <div id="destination-id" class="col-3">
          <MultiSelect
            id="destination-selected"
            filter
            :completeOnFocus="true"
            optionLabel="fullLabel"
            optionValue="code"
            display="chip"
            :maxSelectedLabels="3"
            :placeholder="$t('common.destination')"
            v-model="config.destinationCodes"
            :options="destinationsNew"
            class="w-full"
            :invalid="isInValidForm(config)"
          >
            <template #option="slotProps">
              <p>{{ slotProps.option.fullLabel }}</p>
            </template>
            <template #chip="slotProps">
              <div class="p-chip p-component p-multiselect-chip">
                <div class="p-chip-label">
                  {{ displayDataDestination(slotProps.value) }}
                </div>
                <CloseIcon @click="slotProps.removeCallback($event, slotProps.value)" :customClass="'margin-left-8 cursor-pointer'"></CloseIcon>
              </div>
            </template>
          </MultiSelect>
          <p v-if="isInValidForm(config)" class="text-red-500 pt-1">{{ $t('validate.required') }}</p>
        </div>
        <div id="hotel-code-id" class="col-3">
          <MultiSelect
            filter
            :completeOnFocus="true"
            optionLabel="fullLabel"
            optionValue="hCode"
            display="chip"
            :maxSelectedLabels="3"
            :placeholder="$t('rule_history_log.quote_configuration.max_rooms_config_hotel_code')"
            v-model="config.hotelCodes"
            :options="hotelsNew"
            class="w-full"
            :invalid="isInValidForm(config)"
          >
            <template #option="slotProps">
              <p>{{ slotProps.option.fullLabel }}</p>
            </template>
            <template #chip="slotProps">
              <div class="p-chip p-component p-multiselect-chip">
                <div class="p-chip-label">
                  {{ slotProps.value }}
                </div>
                <CloseIcon @click="slotProps.removeCallback($event, slotProps.value)" :customClass="''"></CloseIcon>
              </div>
            </template>
          </MultiSelect>
          <p v-if="isInValidForm(config)" class="text-red-500 pt-1">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-2">
          <AutoComplete :selectionLimit="200" v-model="config.roomCodes" multiple fluid :typeahead="false" @value-change="updateRoomCategory($event, index)" />
        </div>
        <div class="col-2">
          <div class="flex">
            <InputNumber v-model="config.maxRooms" :min="0" :invalid="isShowValidation && !config.maxRooms" />
            <div class="icon-close-max-room">
              <i class="cursor-pointer pi pi-times-circle text-primary" @click="handleRemoveConfig(index)"></i>
            </div>
          </div>
          <div class="col-10">
            <p v-if="isShowValidation && !config.maxRooms" class="text-red-500 pt-1">{{ $t('validate.min_value') }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="text-center">
      <span id="add-new-max-room" class="cursor-pointer underline text-primary" @click="handleAddNewConfig()">+ {{ $t('common.add') }}</span>
    </div>
    <div class="action-submit text-center">
      <Button id="submit-btn-id" :label="$t('common.buttons.submit')" class="mt-5" @click="handleSubmit()" />
    </div>
  </div>
</template>
